import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';
import OkCancelModal from '@/shared/components/OkCancelModal';
import moment from 'moment';
import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import DatePicker from '@/shared/components/input/DatePicker';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import classNames from 'classnames';
import CurrencySymbol from '@/shared/components/CurrencySymbol';
import Checkbox from '@/shared/components/input/Checkbox';
import DataCacheService from '@/shared/services/DataCacheService';
import {
  falseFunc,
  validateDateRange,
  validateEndDate,
  isBeforeLastYear,
} from '@/shared/utils/validationHelpers';
import config from '../../../../config';

const messages = defineMessages({
  StartPlanning: 'Start planning',
  EndPlanning: 'End planning',
  Schedule: 'Schedule',
  LedgerAccount: 'Ledger account',
  Amount: 'Amount',
  OverrideExistingSchedule: 'Override existing invoice schedule if present.',
  NoSchedule: 'No schedule',
  SetInvoiceSchedule: 'Set invoice schedule',
  InvoiceStreamStartDateInPastExplanation:
    'If you set this start date in the past, its possible that draft invoices are created instantly based on the schedule.',
});

export default class SetInvoiceScheduleModal extends Component {
  state = {
    planningStart: moment(), // Set start date to today
    schedules: [],
    amount: 0,
    ledgerAccount: '',
    overrideExistingSchedule: false,
  };
  static propTypes = {
    show: PropTypes.bool.isRequired,
    intl: intlShape.isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
  };

  static defaultProps = {};

  retrieveSchedules = () => {
    const { intl } = this.props;

    api.base.get(`${config.appUrls.api}/api/invoiceschedule/get`).then(({ data }) => {
      const sortOnLabel = (a, b) => (a.label > b.label) - (a.label < b.label);

      const translatedSchedules = [
        { id: -1, label: intl.formatMessage(messages.NoSchedule) },
        ...data
          .map(t => ({
            ...t,
            label: `${intl.formatMessage(
              listItemMsg('invoiceSchedule', t.id, t.name),
            )}          (${intl.formatMessage(listItemMsg('InvoicingType', t.invoicingType))})`,
          }))
          .sort(sortOnLabel),
      ];

      let selectedSchedule = null;
      if (translatedSchedules && translatedSchedules.length > 0) {
        [selectedSchedule] = translatedSchedules;
      }

      this.setState(state => ({
        ...state,
        schedules: translatedSchedules,
        touched: true,
        selectedSchedule,
      }));
    });
  };

  componentDidMount() {
    this.retrieveSchedules();
  }

  scheduleChanged = event => {
    const { schedules } = this.state;
    const scheduleId = event.target.value;

    const selectedSchedule = scheduleId === -1 ? null : schedules.find(x => x.id === scheduleId);

    let defaultLedger = '';
    if (selectedSchedule) {
      const companySettings = DataCacheService.getDefaultCompany();
      // forfait
      if (selectedSchedule.invoicingType === 1) {
        defaultLedger = companySettings ? companySettings.fixedAmountLedgerAccount : '';
      }
      // advance settlement
      if (selectedSchedule.invoicingType === 2) {
        defaultLedger = companySettings ? companySettings.advanceSettlementLedgerAccount : '';
      }
    }

    this.setState(state => ({
      ...state,
      touched: true,
      ledgerAccount: defaultLedger,
      selectedSchedule,
    }));
  };

  isFormInvalid = () => {
    const { planningStart, planningStop, selectedSchedule, amount } = this.state;
    if (!planningStart) return true;
    if (!validateDateRange(planningStart, planningStop, true)) return true;

    if (
      selectedSchedule &&
      selectedSchedule.id !== -1 &&
      selectedSchedule.invoicingType !== 0 &&
      amount < 1
    ) {
      return true;
    }

    return false;
  };

  amountChanged = value => {
    this.setState(state => ({
      ...state,
      touched: true,
      amount: value,
    }));
  };

  onSave = () => {
    const {
      planningStart,
      planningStop,
      selectedSchedule,
      amount,
      ledgerAccount,
      overrideExistingSchedule,
    } = this.state;

    const { onSave } = this.props;

    this.setState({ touched: true });

    const invoiceSchedule = {
      startDate: planningStart,
      endDate: planningStop,
      scheduleId: !selectedSchedule || selectedSchedule.id === -1 ? null : selectedSchedule.id,
      amount,
      ledgerAccount,
      overrideExistingSchedule,
    };

    return onSave(invoiceSchedule);
  };

  render() {
    const { show, onClose, intl } = this.props;
    const {
      planningStart,
      planningStop,
      planningStartFocused,
      planningStopFocused,
      schedules,
      selectedSchedule,
      amount,
      ledgerAccount,
      touched,
      overrideExistingSchedule,
    } = this.state;

    if (!show) return null;

    const showAmount =
      selectedSchedule && selectedSchedule.id !== -1 && selectedSchedule.invoicingType !== 0;

    const amountClassName = classNames('form-group', {
      'has-error': touched && amount < 1,
    });

    return (
      <OkCancelModal
        title={intl.formatMessage(messages.SetInvoiceSchedule)}
        show={show}
        onClose={onClose}
        disabled={this.isFormInvalid()}
        onSave={() => this.onSave()}
      >
        <div className="form-horizontal">
          <div className="form-group">
            <div className="col-sm-4">
              <label className="control-label ">{intl.formatMessage(messages.Schedule)}</label>
            </div>
            <div className="col-sm-5">
              {schedules && (
                <select
                  onChange={this.scheduleChanged}
                  className="form-control"
                  value={(selectedSchedule && selectedSchedule.id) || undefined}
                >
                  {schedules.map(schedule => (
                    <option key={schedule.id} value={schedule.id}>
                      {schedule.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className={classNames('form-group', { 'has-error': !planningStart })}>
            <div className="col-sm-4">
              <label className="control-label ">{intl.formatMessage(messages.StartPlanning)}</label>
              <div>{intl.formatMessage(messages.InvoiceStreamStartDateInPastExplanation)}</div>
            </div>

            <div className="col-sm-5">
              <DatePicker
                placeholder={intl.formatMessage(messages.StartPlanning)}
                date={planningStart}
                onDateChange={date =>
                  this.setState({ planningStart: date, planningStop: undefined })
                }
                focused={planningStartFocused}
                onFocusChange={({ focused }) => this.setState({ planningStartFocused: focused })}
                id="taskScheduleStartDate"
                required
                block
                small
                isOutsideRange={isBeforeLastYear}
              />
            </div>
          </div>

          <div
            className={classNames('form-group', {
              'has-error': !validateEndDate(planningStop, planningStart),
            })}
          >
            <div className="col-sm-4">
              <label className="control-label">{intl.formatMessage(messages.EndPlanning)}</label>
            </div>
            <div className="col-sm-5">
              <DatePicker
                placeholder={intl.formatMessage(messages.EndPlanning)}
                date={planningStop}
                block
                small
                onDateChange={date => this.setState({ planningStop: date })}
                focused={planningStopFocused}
                onFocusChange={({ focused }) => this.setState({ planningStopFocused: focused })}
                id="taskScheduleEndDate"
                isOutsideRange={falseFunc}
              />
            </div>
          </div>

          {showAmount && (
            <div className={amountClassName}>
              <div className="col-sm-4">
                <label className="control-label">{intl.formatMessage(messages.Amount)}</label>
              </div>
              <div className="col-sm-5">
                <div className="input-group">
                  <input
                    type="number"
                    step="1"
                    value={amount}
                    onChange={e => this.amountChanged(e.target.value)}
                    className="form-control"
                  />
                  <span className="input-group-addon">
                    <CurrencySymbol />
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="form-group">
            <div className="col-sm-4">
              <label className="control-label">{intl.formatMessage(messages.LedgerAccount)}</label>
            </div>
            <div className="col-sm-5">
              <input
                type="text"
                className="form-control"
                name="ledgeraccount"
                maxLength="10"
                value={ledgerAccount}
                onChange={e => this.setState({ ledgerAccount: e.target.value })}
                id="ledgeraccount"
              />
            </div>
          </div>

          <div className="form-group">
            <Checkbox
              extraClass="col-sm-offset-4 col-md-8"
              id="overrideExistingSchedule"
              value={overrideExistingSchedule}
              onClick={() => this.setState({ overrideExistingSchedule: !overrideExistingSchedule })}
              text={intl.formatMessage(messages.OverrideExistingSchedule)}
            />
          </div>
        </div>
      </OkCancelModal>
    );
  }
}
